import React, { useEffect, useState } from 'react';
import { CleverClassroom, getCleverClassrooms } from '../services/CleverStudentImport.service';
import Loader from '../../../components/Common/Loader';
import BackButton from '../../../components/Common/BackButton';

interface CleverClassroomSelectorProps {
  onClassroomSelect: (classroom: CleverClassroom) => void;
  onBack: () => void;
  onClose: () => void;
}

const CleverClassroomSelector: React.FC<CleverClassroomSelectorProps> = ({ onClassroomSelect, onBack, onClose }) => {
  const [classrooms, setClassrooms] = useState<CleverClassroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        setLoading(true);
        const data = await getCleverClassrooms();

        if (data) {
          setClassrooms(data);
        } else {
          setError('Failed to fetch classrooms from Clever');
        }
      } catch (error) {
        setError('Failed to fetch classrooms from Clever');
      } finally {
        setLoading(false);
      }
    };

    fetchClassrooms();
  }, []);

  const filteredClassrooms = classrooms.filter((classroom) =>
    classroom.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <Loader loader={true} dimentions="w-8 h-8" />
        <p className="mt-4 text-gray-600">Loading classrooms from Clever...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={onBack} className="px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto">
          Back
        </button>
      </div>
    );
  }

  if (classrooms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 md:min-w-80 font-sofiaSans">
        <p className="text-gray-600 mb-4">No classrooms found in your Clever account.</p>
        <button onClick={onBack} className="px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-80 font-sofiaSans">
      <div className="text-left">
        <h3 className="w-full flex justify-start text-left text-xl md:text-xl mb-4 font-bold text-storyblue main-title-modal-text">
          Select classroom to import from
        </h3>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search classrooms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none"
        />
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {filteredClassrooms.map((classroom) => (
          <div
            key={classroom.id}
            onClick={() => onClassroomSelect(classroom)}
            className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-secondaryStoryBlackText">{classroom.name}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredClassrooms.length === 0 && searchTerm && (
        <p className="text-secondaryStoryGrayText text-center py-4">No classrooms match your search.</p>
      )}

      <div className="flex justify-between pt-4">
        <BackButton onClick={onBack}></BackButton>
      </div>
    </div>
  );
};

export default CleverClassroomSelector;
